import React, { useState, useEffect } from 'react';
import { notificationApi, NotificationData, CreateNotificationRequest } from '../../services/notificationApi';
import styles from '../../styles/AdminPanel.module.css';

const NotificationManagement: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    walletAddress: '',
    type: '' as '' | 'info' | 'warning' | 'success' | 'announcement',
    isRead: '' as '' | 'true' | 'false',
  });

  // Form state for creating notifications
  const [createForm, setCreateForm] = useState<CreateNotificationRequest>({
    targetWalletAddress: '',
    title: '',
    content: '',
    type: 'info',
    expiresAt: '',
  });
  const [createLoading, setCreateLoading] = useState(false);

  const itemsPerPage = 20;

  // Load notifications
  const loadNotifications = async () => {
    setLoading(true);
    try {
      const apiFilters: any = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };

      if (filters.walletAddress) apiFilters.walletAddress = filters.walletAddress;
      if (filters.type) apiFilters.type = filters.type;
      if (filters.isRead) apiFilters.isRead = filters.isRead === 'true';

      const result = await notificationApi.getAllNotifications(apiFilters);
      setNotifications(result.notifications);
      setTotal(result.total);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      alert('Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load notifications on mount and when filters change
  useEffect(() => {
    loadNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle form input changes
  const handleFormChange = (key: keyof CreateNotificationRequest, value: string) => {
    setCreateForm(prev => ({ ...prev, [key]: value }));
  };

  // Create notification
  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      // Validate form
      if (!createForm.targetWalletAddress || !createForm.title || !createForm.content) {
        alert('Please fill in all required fields');
        return;
      }

      if (!/^0x[a-fA-F0-9]{40}$/.test(createForm.targetWalletAddress)) {
        alert('Invalid wallet address format');
        return;
      }

      // Create notification
      const notificationData = {
        ...createForm,
        expiresAt: createForm.expiresAt || undefined, // Convert empty string to undefined
      };
      await notificationApi.createNotification(notificationData);
      
      // Reset form
      setCreateForm({
        targetWalletAddress: '',
        title: '',
        content: '',
        type: 'info',
        expiresAt: '',
      });
      setShowCreateForm(false);
      
      // Reload notifications
      await loadNotifications();
      
      alert('Notification created successfully!');
    } catch (error: any) {
      console.error('Failed to create notification:', error);
      alert(`Failed to create notification: ${error.message || 'Please try again.'}`);
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await notificationApi.deleteNotification(notificationId);
      await loadNotifications();
      alert('Notification deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      alert(`Failed to delete notification: ${error.message || 'Please try again.'}`);
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case 'warning': return '#f59e0b';
      case 'success': return '#10b981';
      case 'announcement': return '#8b5cf6';
      case 'info':
      default: return '#3b82f6';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'announcement': return '📢';
      case 'info':
      default: return 'ℹ️';
    }
  };

  return (
    <div className={styles.adminSection}>
      <div className={styles.adminSectionHeader}>
        <h2>🔔 Notification Management</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className={`${styles.btn} ${styles.btnPrimary}`}
        >
          {showCreateForm ? 'Cancel' : '+ Create Notification'}
        </button>
      </div>

      {/* Create Notification Form */}
      {showCreateForm && (
        <div className={styles.formContainer}>
          <h3>Create New Notification</h3>
          <form onSubmit={handleCreateNotification} className={styles.createForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Target Wallet Address*</label>
                <input
                  type="text"
                  placeholder="0x..."
                  value={createForm.targetWalletAddress}
                  onChange={(e) => handleFormChange('targetWalletAddress', e.target.value)}
                  className={styles.formInput}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Type*</label>
                <select
                  value={createForm.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  className={styles.formInput}
                  required
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                  <option value="announcement">Announcement</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Title*</label>
              <input
                type="text"
                placeholder="Notification title..."
                value={createForm.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                className={styles.formInput}
                maxLength={200}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Content* (Markdown supported)</label>
              <textarea
                placeholder="Notification content... You can use **bold**, *italic*, `code`, and other markdown formatting."
                value={createForm.content}
                onChange={(e) => handleFormChange('content', e.target.value)}
                className={styles.formTextarea}
                rows={6}
                maxLength={5000}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Expires At (Optional)</label>
              <input
                type="datetime-local"
                value={createForm.expiresAt}
                onChange={(e) => handleFormChange('expiresAt', e.target.value)}
                className={styles.formInput}
              />
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createLoading}
                className={`${styles.btn} ${styles.btnPrimary}`}
              >
                {createLoading ? 'Creating...' : 'Create Notification'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <label>Wallet Address</label>
            <input
              type="text"
              placeholder="0x..."
              value={filters.walletAddress}
              onChange={(e) => handleFilterChange('walletAddress', e.target.value)}
              className={styles.filterInput}
            />
          </div>
          <div className={styles.filterGroup}>
            <label>Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className={styles.filterInput}
            >
              <option value="">All Types</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="announcement">Announcement</option>
            </select>
          </div>
          <div className={styles.filterGroup}>
            <label>Read Status</label>
            <select
              value={filters.isRead}
              onChange={(e) => handleFilterChange('isRead', e.target.value)}
              className={styles.filterInput}
            >
              <option value="">All</option>
              <option value="true">Read</option>
              <option value="false">Unread</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className={styles.dataContainer}>
        {loading ? (
          <div className={styles.loading}>Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className={styles.emptyState}>No notifications found</div>
        ) : (
          <div className={styles.notificationList}>
            {notifications.map((notification) => (
              <div key={notification.id} className={styles.notificationItem}>
                <div className={styles.notificationHeader}>
                  <div className={styles.notificationInfo}>
                    <span
                      className={styles.notificationIcon}
                      style={{ color: getNotificationTypeColor(notification.type) }}
                    >
                      {getNotificationIcon(notification.type)}
                    </span>
                    <div className={styles.notificationTitle}>{notification.title}</div>
                    <div className={styles.notificationMeta}>
                      <span className={styles.notificationWallet}>
                        {notification.targetWalletAddress}
                      </span>
                      <span className={`${styles.notificationStatus} ${notification.isRead ? styles.read : styles.unread}`}>
                        {notification.isRead ? 'Read' : 'Unread'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.notificationActions}>
                    <span className={styles.notificationDate}>
                      {new Date(notification.createdAt).toLocaleString()}
                    </span>
                    <button
                      onClick={() => handleDeleteNotification(notification.id)}
                      className={`${styles.btn} ${styles.btnDanger} ${styles.btnSmall}`}
                      title="Delete notification"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className={styles.notificationContent}>
                  {notification.content.length > 200
                    ? `${notification.content.substring(0, 200)}...`
                    : notification.content}
                </div>
                {notification.readAt && (
                  <div className={styles.notificationReadAt}>
                    Read at: {new Date(notification.readAt).toLocaleString()}
                  </div>
                )}
                {notification.expiresAt && (
                  <div className={styles.notificationExpires}>
                    Expires: {new Date(notification.expiresAt).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {currentPage} of {totalPages} ({total} total)
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;